import { Alert, Card, Col, Row, Typography } from 'antd';
import { Link } from 'react-router-dom';
import {
  ReadOutlined,
  PercentageOutlined,
  CalendarOutlined,
  FileTextOutlined,
  SettingOutlined,
  NumberOutlined,
  GoldOutlined,
  BookOutlined,
  AreaChartOutlined,
  ProfileOutlined,
  HistoryOutlined,
  AppstoreOutlined,
  WalletOutlined,
  DollarOutlined,
  // Stage 6B.7E — Profit & Loss + Balance Sheet.
  LineChartOutlined,
  AccountBookOutlined
} from '@ant-design/icons';
import { ErpMasterShell } from '../../../components/common/erp-grid';
import './accounts.css';

const { Title, Paragraph } = Typography;

// Stage 6B.1 — Accounts module landing page.
// Foundation only: links to the four sub-pages + a banner that
// explicitly flags this as a draft surface (no posting engine, no
// live GST filing).
const SECTIONS = [
  {
    to: '/erp/accounts/setup',
    title: 'Setup & Readiness',
    desc:  'Books setup checklist: financial year, default GSTIN, filing scheme, document sequences, opening balances. Start here for a new tenant.',
    icon:  <SettingOutlined />
  },
  {
    to: '/erp/accounts/ledgers',
    title: 'Ledger Accounts',
    desc:  'Chart of Accounts. System ledgers (Cash, Bank, Sundry Debtors, GST Input/Output, …) are seeded per tenant.',
    icon:  <ReadOutlined />
  },
  {
    to: '/erp/accounts/taxes',
    title: 'Tax Rates',
    desc:  'GST rate master with effective-date versioning. Standard 0/5/12/18/28 bands + exempt / nil-rated / non-GST are seeded.',
    icon:  <PercentageOutlined />
  },
  {
    to: '/erp/accounts/periods',
    title: 'Accounting Periods',
    desc:  'Fiscal calendar (month / quarter / year). Periods can be open, locked, or closed — locking blocks future voucher posting.',
    icon:  <CalendarOutlined />
  },
  {
    to: '/erp/accounts/document-sequences',
    title: 'Document Sequences',
    desc:  'Per-doc-type running numbers (sales invoice, purchase bill, journal voucher, …). Prefix / padding / reset frequency are operator-configurable.',
    icon:  <NumberOutlined />
  },
  {
    to: '/erp/accounts/opening-balances',
    title: 'Opening Balances',
    desc:  'Trial-balance entry per ledger. The system shows total debit vs total credit and flags any imbalance for review.',
    icon:  <GoldOutlined />
  },
  {
    to: '/erp/accounts/vouchers',
    title: 'Journal Vouchers',
    desc:  'Double-entry journal entries. Draft → Post (Approval-MFA gated) updates ledger balances; Cancel reverses them. First transactional accounting surface.',
    icon:  <BookOutlined />
  },
  // Stage 6B.6 — first money-movement module.
  {
    to: '/erp/accounts/payments',
    title: 'Payment / Receipt',
    desc:  'Supplier payments + customer receipts in one register. Cash / Bank / UPI / Cheque / NEFT / RTGS / IMPS modes. Posts through the voucher engine.',
    icon:  <WalletOutlined />
  },
  // Stage 6B.7 — Expense entry (paid + credit, GST-aware).
  {
    to: '/erp/accounts/expenses',
    title: 'Expense Entry',
    desc:  'Record paid + credit expenses with GST input tracking. Auto-splits CGST/SGST/IGST and posts through the voucher engine.',
    icon:  <DollarOutlined />
  },
  // Stage 6B.5 — read-only account reports. All four consume the
  // balances maintained by the Stage 6B.4 posting engine.
  {
    to: '/erp/accounts/reports/trial-balance',
    title: 'Trial Balance',
    desc:  'Live trial balance from ledger_accounts.current_balance. Debit-positive sign convention; flags any debit/credit mismatch.',
    icon:  <AreaChartOutlined />
  },
  {
    to: '/erp/accounts/reports/ledger-statement',
    title: 'Ledger Statement',
    desc:  'Per-ledger voucher line history with opening / closing balance and running balance per row. Filter by date range.',
    icon:  <ProfileOutlined />
  },
  {
    to: '/erp/accounts/reports/voucher-register',
    title: 'Voucher Register',
    desc:  'Day Book — chronological list of voucher headers. Filter by status / type / date / narration. Posted-only by default.',
    icon:  <HistoryOutlined />
  },
  {
    to: '/erp/accounts/reports/account-balances',
    title: 'Account Balances',
    desc:  'Grouped balances by account type + group. Foundation for the future Profit & Loss and Balance Sheet reports.',
    icon:  <AppstoreOutlined />
  },
  // Stage 6B.7E — Profit & Loss + Balance Sheet (read-only).
  {
    to: '/erp/accounts/reports/profit-loss',
    title: 'Profit & Loss',
    desc:  'Income statement for the selected date range. Posted vouchers only; operator-positive magnitudes; net profit / loss badge.',
    icon:  <LineChartOutlined />
  },
  {
    to: '/erp/accounts/reports/balance-sheet',
    title: 'Balance Sheet',
    desc:  'Statement of financial position: assets vs liabilities + equity (live balances). Current period P&L is shown separately on the equity side.',
    icon:  <AccountBookOutlined />
  },
  {
    to: '/erp/accounts/gst-reports',
    title: 'GST Reports',
    desc:  'GSTR-1 / 3B / HSN / ITC / GST payable summaries. For operator review only — not filed to the GST portal.',
    icon:  <FileTextOutlined />
  }
];

export default function AccountsHomePage() {
  return (
    <ErpMasterShell>
      <div className="erp-accounts-page erp-accounts-home">
        <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>Accounts</Title>
        <Alert
          type="info"
          showIcon
          banner
          style={{ marginBottom: 12 }}
          message="Foundation module"
          description={
            <span>
              Chart of Accounts, tax-rate master, fiscal periods, vouchers,
              payment / receipt, expense entries and read-only reports are
              available. Full Sales / Purchase / Inventory transaction
              screens and the live GSTR portal submission flow land in later
              stages.
            </span>
          }
        />
        <Row gutter={[12, 12]}>
          {SECTIONS.map((s) => (
            <Col key={s.to} xs={24} md={12} xl={8}>
              <Link to={s.to} style={{ display: 'block' }}>
                <Card hoverable size="small">
                  <Title level={5} style={{ marginTop: 0 }}>
                    <span style={{ marginRight: 8 }}>{s.icon}</span>{s.title}
                  </Title>
                  <Paragraph type="secondary" style={{ margin: 0 }}>
                    {s.desc}
                  </Paragraph>
                </Card>
              </Link>
            </Col>
          ))}
        </Row>
      </div>
    </ErpMasterShell>
  );
}
